// src/context/AuthContext.jsx
// Authentication context that uses the globally loaded authUtils.web.js
// Note: authUtils.web.js is loaded via script tag in index.web.html
// It wraps the global fetch and provides token management

import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use the same API URL detection as authUtils
  const getApiUrl = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:1019';
    }
    return 'https://api.synthomaticai.com';
    // return 'https://eyeq-cloud-api.onrender.com';
  };

  const API_URL = `${getApiUrl()}/api/v1`;

  // Helper functions to access localStorage directly
  // authUtils.web.js wraps fetch globally, so we just need token storage helpers
  const getAccessToken = () => {
    return localStorage.getItem('access_token');
  };

  const setAccessToken = (token) => {
    localStorage.setItem('access_token', token);
    
    // Notify camera service if available (from authUtils)
    if (typeof window !== 'undefined' && window.cameraService) {
      try {
        window.cameraService.onTokenRefresh();
      } catch (error) {
        console.warn('[Auth] Failed to refresh camera streams:', error);
      }
    }
  };

  const clearAuthTokens = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    
    // Clear camera streams if available (from authUtils)
    if (typeof window !== 'undefined' && window.cameraService) {
      try {
        window.cameraService.clearAllCameraStreams();
      } catch (error) {
        console.warn('[Auth] Failed to clear camera streams:', error);
      }
    }
  };

  // Helper to decode JWT and extract user info
  const decodeToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('[Auth] Failed to decode token:', error);
      return null;
    }
  };

  // Check for existing token on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getAccessToken();
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (err) {
          console.error('[Auth] Failed to parse stored user:', err);
          clearAuthTokens();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (username, password) => {
    setError(null);
    setLoading(true);
    
    try {
      console.log('[Auth] Attempting login to:', `${API_URL}/auth/login`);
      
      // Use global fetch (wrapped by authUtils.web.js loaded via script tag)
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Login failed');
      }

      const data = await response.json();
      console.log('[Auth] Login response:', data);
      
      // Handle both response formats
      const accessToken = data.data?.access_token || data.access_token;
      
      if (!accessToken) {
        throw new Error('No access token received from server');
      }

      // Store token
      setAccessToken(accessToken);
      
      // Decode token to get user info
      const tokenPayload = decodeToken(accessToken);
      const userData = {
        username: tokenPayload?.sub || username,
        roles: tokenPayload?.roles || ['user'],
        user_id: tokenPayload?.user_id
      };
      
      console.log('[Auth] User data from token:', userData);
      
      // Store user info
      localStorage.setItem('user', JSON.stringify(userData));
      
      setUser(userData);
      setLoading(false);
      return { success: true };
    } catch (err) {
      console.error('[Auth] Login error:', err);
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  const logout = () => {
    clearAuthTokens();
    setUser(null);
    
    // Optionally call backend logout endpoint
    fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(err => console.error('[Auth] Logout error:', err));
  };

  const getAuthHeader = () => {
    const token = getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      error, 
      login, 
      logout, 
      getAuthHeader,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};